/**
 * Purchase Limits — checkout click gate.
 *
 * The only JS consumer, and deliberately a thin one. The cart page and the cart
 * drawer are both rendered by Liquid on every change — the cart page reloads on
 * a quantity change, the drawer is re-injected from the Section Rendering API —
 * so purchase-limits-check.liquid already disables the checkout button and
 * renders the messages. That gate works with JS turned off.
 *
 * This module exists for the case where the rendered state is stale: a cached
 * section, a third party script mutating the cart, a re-render that did not
 * happen. It never renders the button state itself, so it cannot disagree with
 * Liquid about what the DOM should look like — it only refuses the click.
 *
 * Deliberately does NOT fetch the cart on load. Before the first cart:change,
 * the server rendered state is by definition fresh, so there is nothing to
 * second guess and no request worth making. After a cart:change we get the cart
 * JSON handed to us for free.
 */

import { evaluateRules } from './purchase-limits-engine.js';
import { getRules } from './purchase-limits-rules-source.js';
import { getCustomerState } from './purchase-limits-customer-state.js';
import { getConfig, getMessage } from './purchase-limits-config.js';

const CHECKOUT_BUTTON_SELECTOR = 'button[name="checkout"]';

/** @type {Object|null} the most recent cart JSON, null until the first cart:change */
let latestCart = null;

/**
 * Sum line item quantities per scope.
 *
 * @param {Object} cart  cart JSON as returned by the AJAX cart API
 * @returns {{box: number, op: number}}
 */
function sumQuantities(cart) {
	const { boxVariantIds, opVariantIds } = getConfig();
	const quantities = { box: 0, op: 0 };

	(cart.items || []).forEach((item) => {
		const variantId = item.variant_id ?? item.id;

		if (boxVariantIds.includes(variantId)) {
			quantities.box += item.quantity;
		} else if (opVariantIds.includes(variantId)) {
			quantities.op += item.quantity;
		}
	});

	return quantities;
}

/**
 * Cart rules violated by the cart we last saw.
 *
 * Returns an empty array while no cart has been observed — the server rendered
 * gate is authoritative until something changes, and blocking on a guess would
 * be worse than trusting it.
 *
 * @returns {Array<Object>} violations
 */
function currentViolations() {
	if (latestCart === null) {
		return [];
	}

	return evaluateRules(
		getRules(),
		{ customer: getCustomerState(), cartQuantities: sumQuantities(latestCart) },
		'cart',
	).filter((violation) => violation.effect === 'block');
}

/**
 * Tell the customer why the click did nothing.
 *
 * The Liquid rendered message is the normal channel; this only fires in the
 * stale case, where that message may not be on screen.
 *
 * @param {Array<Object>} violations
 */
function announce(violations) {
	const message = violations
		.map((violation) => (violation.messageKey ? getMessage(violation.messageKey) : ''))
		.filter(Boolean)[0];

	if (!message) {
		return;
	}

	document.dispatchEvent(new CustomEvent('toast:show', { detail: { message, tone: 'error' } }));
}

document.addEventListener('cart:change', (event) => {
	latestCart = event.detail?.cart ?? null;
});

// cart:refresh carries no payload. What we hold is now unverifiable, so drop it
// and fall back to trusting the freshly rendered markup.
document.addEventListener('cart:refresh', () => {
	latestCart = null;
});

// Capture phase on document: fires before the form submits and survives every
// re-render, because it is not bound to any button the theme replaces.
document.addEventListener(
	'click',
	(event) => {
		if (!event.target.closest?.(CHECKOUT_BUTTON_SELECTOR)) {
			return;
		}

		const violations = currentViolations();

		if (violations.length === 0) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		announce(violations);
	},
	true,
);
